// Pure logic for the shared CardCollection shell (ADR-0010 / plan §1, §3).
//
// This module is SOURCE-AGNOSTIC: it owns the shared card-item contract plus the
// search-side adapter and the pagination math (shared with the want page's
// numbered pages).
//
// The card-item model is the contract a page adapts its cards to, so the shell
// renders them without knowing the source:
//
//   {
//     key,                            // stable id
//     name,
//     set, collectorNumber, finish,   // the printing
//     rarity, typeLine,               // for the shared filters
//     price,                          // number; a Sample Card's "from" floor
//     isSample,                       // true for a representative (name-level)
//     imageRef,                       // { scryfallId } | full Scryfall payload
//     holders: [{ ownerName, binderName, binderUrl, quantity?, price?, shared? }],
//   }

import { buildBinderUrl } from './search-logic.js';

/**
 * Adapt a search `printing` (from regroupByPrinting) to the card-item contract.
 * A printing is always a concrete pinned printing, so it is never a Sample Card.
 * @param {Object} printing
 * @returns {Object} card-item
 */
export function printingToItem(printing) {
  const holders = (printing.binders || []).map((b) => ({
    ownerName: b.ownerName || null,
    binderName: b.binderName || null,
    binderUrl: buildBinderUrl(b.source, b.sourceType, b.sourceId),
    quantity: b.quantity,
    // Group search lists one entry per Claim; false marks the viewer's own
    // Claim where they are dark ("not shared here", ADR-0021 decision 4).
    shared: b.shared !== false,
  }));

  return {
    key: printing.key,
    name: printing.name,
    set: printing.set ?? null,
    collectorNumber: printing.collectorNumber ?? null,
    finish: printing.finish ?? null,
    rarity: printing.rarity || 'common',
    typeLine: printing.typeLine || '',
    price: printing.price || 0,
    isSample: false,
    // The search payload carries the full Scryfall card object (image_uris etc.);
    // pass it through so getCardImageUrl uses the richest source available.
    imageRef:
      printing.imageData || (printing.scryfallId ? { scryfallId: printing.scryfallId } : null),
    holders,
  };
}

// ---------------------------------------------------------------------------
// Pagination (pure)
// ---------------------------------------------------------------------------

/** Number of pages for `total` items at `pageSize` per page. */
export function pageCount(total, pageSize) {
  if (!pageSize || pageSize <= 0) return 0;
  return Math.ceil((total || 0) / pageSize);
}

/** Clamp a requested page into [1, lastPage] (or 1 when there are no pages). */
export function clampPage(page, lastPage) {
  if (!lastPage || lastPage < 1) return 1;
  if (page < 1) return 1;
  if (page > lastPage) return lastPage;
  return page;
}

// ---------------------------------------------------------------------------
// Price formatting (pure) — shared by CardRow, CardTile, and CardHolders so the
// "$X.XX" / "from $X.XX" treatment lives in one place (review §4).
// ---------------------------------------------------------------------------

/**
 * Format a price as `<symbol><amount.2dp>`, or '' for a non-positive/absent
 * price (the empty string is how the row/tile/holders all suppress a $0 price).
 * The Sample Card's "from" prefix is rendered by the CardPrice component, not
 * folded in here, so this stays a pure number→string formatter.
 * @param {number|null|undefined} price
 * @param {string} currencySymbol
 * @returns {string}
 */
export function formatPrice(price, currencySymbol = '$') {
  if (price == null || !(price > 0)) return '';
  return `${currencySymbol}${price.toFixed(2)}`;
}
